import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  ILogger,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef } from "react";
import config from "../Config/config.json";

// Real-time order/notification updates pushed by the backend (ASP.NET Core SignalR).
// One shared connection per logged-in session; pages subscribe with useVendorOrdersHub.

export interface VendorHubEvent {
  // Hub method name the server invoked, e.g. "ReceiveOrderUpdate"
  name: string;
  // Arguments sent with it; empty for events not listed in KNOWN_EVENTS (see below)
  args: any[];
}

type Listener = (event: VendorHubEvent) => void;

// Event names confirmed with the backend; their payloads reach listeners.
// Events not listed here still reach listeners (without payload) via the logger below,
// and are logged to the console so they can be added.
const KNOWN_EVENTS: string[] = [];

const listeners = new Set<Listener>();
let connection: HubConnection | null = null;

const emit = (event: VendorHubEvent) => {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (e) {
      console.error("Vendor hub listener failed:", e);
    }
  });
};

// The SignalR client logs a warning when the server calls a method nobody registered.
// Turning that into an event means every server push triggers a refresh, even before
// its name is known.
const hubLogger: ILogger = {
  log(level: LogLevel, message: string) {
    const unhandled = /No client method with the name '([^']+)' found/i.exec(message);
    if (unhandled) {
      console.info(`[vendor-orders hub] event "${unhandled[1]}" received - add it to KNOWN_EVENTS to get its payload`);
      emit({ name: unhandled[1], args: [] });
      return;
    }
    if (level >= LogLevel.Warning) {
      console.warn(`[vendor-orders hub] ${message}`);
    }
  },
};

// Lets other code (e.g. the notification check that runs every 10 seconds) tell the order lists to
// reload, the same way a push from the hub does
export const triggerVendorOrdersRefresh = () => emit({ name: "refresh", args: [] });

export const startVendorOrdersHub = async () => {
  const token = localStorage.getItem("token");
  if (!token || !config.vendorOrdersHubUrl) return;
  if (connection && connection.state !== HubConnectionState.Disconnected) return;

  connection = new HubConnectionBuilder()
    .withUrl(config.vendorOrdersHubUrl, {
      // Read on every (re)connect so a refreshed login token is picked up
      accessTokenFactory: () => localStorage.getItem("token") || "",
      // Server-Sent Events is left out on purpose: behind the API's nginx proxy the SSE stream
      // is buffered, the handshake times out on the server ("Handshake was canceled") and the
      // client throws an uncaught error. Long polling is the fallback when WebSockets fail.
      transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect()
    .configureLogging(hubLogger)
    .build();

  KNOWN_EVENTS.forEach((name) => connection!.on(name, (...args: any[]) => emit({ name, args })));

  try {
    await connection.start();
    // Handy when checking whether live updates work: open the browser console and look for this
    console.info("[vendor-orders hub] connected");
  } catch (e: any) {
    // Non-fatal: the app keeps working without live updates
    console.warn("[vendor-orders hub] could not connect:", e?.message || e);
  }
};

export const stopVendorOrdersHub = async () => {
  const current = connection;
  connection = null;
  if (current) {
    await current.stop().catch(() => undefined);
  }
};

// Calls onEvent for every push from the hub while the component is mounted
export const useVendorOrdersHub = (onEvent: Listener) => {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    const listener: Listener = (event) => handlerRef.current(event);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
};
